import { useEffect, useState } from 'react';
import { t } from '../lib/i18n';
import { useRealtime } from '../app/realtime-store';

/**
 * Somebody's face, or their initials.
 *
 * The fallback is the point. An avatar URL is signed and short-lived, the file can be deleted, and
 * the network can fail — and an `<img>` that fails renders nothing at all, leaving a hole where a
 * person should be. So a failure falls back to initials, which is what somebody with no picture
 * gets anyway.
 *
 * Initials sit on a colour derived from the name rather than on one shared background. A column of
 * six identical grey circles tells a reader nothing; six different ones are scannable, and the same
 * person keeps their colour everywhere because the seed is the name — including on rows that carry
 * a name and no id, like an audit entry about somebody since removed.
 */

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg';

/** Hues legible under white text at every size, and far enough apart to read as different. */
const HUES = [210, 340, 150, 35, 265, 190, 15, 95];

function hueFor(seed: string): number {
  let hash = 0;

  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) % 100_000;
  }

  return HUES[hash % HUES.length]!;
}

export function Avatar({
  name,
  url,
  size = 'lg',
  title,
}: {
  name: string;
  url?: string | null;
  size?: AvatarSize;
  /** Defaults to the name; pass `null` where the name is already beside it and would be read twice. */
  title?: string | null;
}): React.JSX.Element {
  const [failed, setFailed] = useState(false);

  // A new URL deserves a new attempt: the old one may simply have expired.
  useEffect(() => {
    setFailed(false);
  }, [url]);

  const label = title === undefined ? name : (title ?? undefined);
  const showImage = Boolean(url) && !failed;

  return (
    <span
      className={`avatar avatar--${size}${showImage ? '' : ' avatar--initials'}`}
      {...(showImage ? {} : { style: { '--avatar-hue': hueFor(name) } as React.CSSProperties })}
      {...(label ? { title: label } : {})}
    >
      {showImage ? (
        <img src={url!} alt="" loading="lazy" onError={() => setFailed(true)} />
      ) : (
        <span aria-hidden="true">{initials(name)}</span>
      )}
    </span>
  );
}

/**
 * Two letters: a name is personal data, and this is as much as a header needs.
 *
 * First and last word rather than the first two, because "Maria da Silva" is M+S to a reader and
 * M+D to a naive split — and "da" tells nobody anything.
 */
export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();

  return `${parts[0]![0]}${parts.at(-1)![0]}`.toUpperCase();
}

/**
 * A picture and a name together, which is how a person should read in a cell.
 *
 * One component rather than two tags side by side in forty places, so the gap, the alignment and the
 * truncation are decided once. A long name truncates rather than wrapping: a row that grows to two
 * lines because of one name re-flows the whole table.
 */
export function Person({
  name,
  url,
  size = 'sm',
  secondary,
  muted = false,
  userId,
}: {
  name: string;
  url?: string | null;
  size?: AvatarSize;
  /** The line under the name — an e-mail, a role — where there is room for one. */
  secondary?: string | null;
  /** For "sem responsável" and the like: the shape of a person, without claiming to be one. */
  muted?: boolean;
  /**
   * Given an id, the avatar says whether they are connected right now.
   *
   * Opt-in rather than automatic: "is Ana online" is worth showing beside a colleague and is noise
   * beside the author of a note from three weeks ago.
   */
  userId?: string | null;
}): React.JSX.Element {
  return (
    <span className="person">
      <span className="person__avatar">
        <Avatar name={name} url={url} size={size} title={null} />
        {userId && <PresenceDot userId={userId} />}
      </span>

      <span className="person__text">
        <span className={`person__name${muted ? ' muted' : ''}`}>{name}</span>
        {secondary && <span className="person__secondary">{secondary}</span>}
      </span>
    </span>
  );
}

/**
 * Connected, or not.
 *
 * Driven by the one socket the tab already holds, so this costs nothing per avatar — the set of who
 * is online is pushed once and every dot reads the same set. A title rather than only a colour,
 * because a dot is exactly the kind of signal somebody cannot see (WCAG 2.2 AA, context.md §127).
 */
export function PresenceDot({ userId }: { userId: string }): React.JSX.Element {
  const { online } = useRealtime();
  const isOnline = online.has(userId);

  return (
    <span
      className={`presence-dot${isOnline ? ' presence-dot--on' : ''}`}
      title={isOnline ? t('presence.online') : t('presence.offline')}
      role="img"
      aria-label={isOnline ? t('presence.online') : t('presence.offline')}
    />
  );
}

/**
 * Several people, overlapping, with the rest as a count.
 *
 * For a watcher list or an attendee list, where the question is "who is on this" rather than "who
 * exactly are all eleven of them".
 */
export function AvatarGroup({
  people,
  max = 4,
  size = 'xs',
}: {
  people: { name: string; url?: string | null }[];
  max?: number;
  size?: AvatarSize;
}): React.JSX.Element | null {
  if (people.length === 0) return null;

  const shown = people.slice(0, max);
  const rest = people.length - shown.length;

  return (
    <span className="avatar-group">
      {shown.map((person, index) => (
        <Avatar key={`${person.name}-${index}`} name={person.name} url={person.url} size={size} />
      ))}

      {rest > 0 && (
        <span className={`avatar avatar--${size} avatar--more`} aria-hidden="true">
          {`+${rest}`}
        </span>
      )}
    </span>
  );
}
